import { Box, Typography, Button, Tab, Tabs } from '@mui/material'
import { FormatDateValue } from 'core/Utilities'
import { type ReferralData } from 'Pages/Referral/ReferralDetail'
import { usePermissions } from 'hooks/use-permissions'
import stopwatch from 'Assets/Icons/stopwatch.png'
import printer from 'Assets/Icons/printer.png'
import { ChildDetails } from '.'
import { ReferralNotes } from 'Pages/Referral/ReferralNotes'
import { type CareNetworkCommunication, ChildAuditAccessTypeEnum, type DocumentTypeRequirement, type postError, AccountType, NoteType } from 'core/types'
import { ReferralDocuments } from 'Pages/Referral/ReferralDocuments'
import { TabDisplay } from 'Components/TabDisplay'
import { useState } from 'react'
import { EnrollmentNotes } from 'Pages/ServiceProvider/Enrollments/EnrollmentNotes'
import { useAuth } from 'hooks/use-auth'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import { useErrorDispatch } from 'ContextProviders/ErrorProvider'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { CommunicationDetail } from 'Pages/CareNetworkManagement/CareNetworkDetail/CommunicationDetail'
import { ChildAndGuardianDocumentList } from 'Components/DocumentList/ChildAndGuardianDocumentList'
import AddIcon from '@mui/icons-material/Add'
import { SupplementalNutritionList } from 'Pages/CareNetworkManagement/CareNetworkDetail/SupplementalNutritionList'
import { ReferralHeader } from 'Components/ChildDetails/ReferralHeader'
import { EnrollmentHeader } from 'Components/ChildDetails/EnrollmentHeader'
import { RestraintEdit } from 'Pages/RestraintEvents/RestraintEdit'
import ViewListIcon from '@mui/icons-material/ViewList'
import { BannerModal } from 'Components/Modal/BannerModal'
import { InterestFormCard } from 'Components/Cards/InterestFormCard'
import { CareManagementSummary } from 'Components/CareManagement/CareManagementSummary'
export interface ReferralViewProps {
  hideIncomeReapply?: boolean
  enrollmentId?: number
  referral: ReferralData
  onPrint: () => void
  docTypes?: DocumentTypeRequirement[]
  type: 'referral' | 'enrollment'
  hideEditButtons: boolean
  onRefresh: () => void
  onEdit: (show: boolean) => void
  onCreateNew?: (show: boolean) => void
  onEditHouseholdMembers: (show: boolean) => void
}

export function ReferralView (props: ReferralViewProps): JSX.Element {
  const { user } = useAuth()
  const [isRequestLoading, setIsRequestLoading] = useState(false)
  const [reminderUsers, setReminderUsers] = useState<Array<{ id: number, name: string }>>([])
  const account = useAccountId()
  const dispatch = useErrorDispatch()
  const perms = usePermissions()
  const [value, setValue] = useState(0)
  const [showReminderModel, setShowReminderModel] = useState(false)
  const [reminderRecord, setReminderRecord] = useState<CareNetworkCommunication | null>(null)
  const [logRestraint, setLogRestraint] = useState(false)
  const [showIntakeDetails, setShowIntakeDetails] = useState(false)
  const handleReminderClose = (): void => {
    setShowReminderModel(false)
  }

  const handleReminderAccept = async (com: CareNetworkCommunication): Promise<void> => {
    dispatch({ type: 'clear', errors: [] })
    if (!isRequestLoading) {
      setIsRequestLoading(true)
      const result = await sendPost('/Communication/CreateCommunication', com)
      const rsp: { success: boolean, errors: postError[] } = result.response
      if (rsp.success) {
        setShowReminderModel(false)
        const userindex = reminderUsers.findIndex(x => x.id === com.userID)
        toast.success(`Communication record was updated. A reminder will be sent on ${FormatDateValue(com.reminderDate ?? new Date())} to ${reminderUsers[userindex].name}`)
      } else {
        dispatch({ type: 'update', errors: rsp.errors })
      }
      setIsRequestLoading(false)
    }
  }

  const setReminder = async (): Promise<void> => {
    switch (account?.type) {
      case AccountType.ISD: {
        const { response } = await sendGet(`/ISDStaff/GetStaffList?isdID=${account?.id}`, {})
        if (response !== null) {
          setReminderUsers(response)
        }
      }
        break
      case AccountType.SERVICE_PROVIDER: {
        const { response } = await sendGet(`/ServiceProviderSettings/GetProviderStaff?spID=${account?.id ?? 0}`, {})
        if (response !== null) {
          const users: Array<{ id: number, name: string }> = response.map((a: { id: number, firstName: string, lastName: string }) => {
            return { id: a.id, name: `${a.firstName} ${a.lastName}` }
          })
          setReminderUsers(users)
        }
      }
        break
      case AccountType.INTAKE_USER: {
        const { response } = await sendGet(`/ServiceProviderSettings/GetIntakeUserStaff?spID=${account?.id ?? 0}`, {})
        if (response !== null) {
          const users: Array<{ id: number, name: string }> = response.map((a: { id: number, firstName: string, lastName: string }) => {
            return { id: a.id, name: `${a.firstName} ${a.lastName}` }
          })
          setReminderUsers(users)
        }
      }
        break
    }

    const initialRecord: CareNetworkCommunication = {
      userID: user?.id ?? 0,
      addressedWhatMatteredMost: null,
      caregiverID: null,
      contactMethodID: null,
      enteredOn: new Date(),
      id: 0,
      type: NoteType.Reminder,
      childID: props.referral?.childID ?? 0,
      needMet: null,
      note: '',
      reasonNotMet: '',
      reminderDate: null,
      referralID: null,
      enrollmentID: props.enrollmentId ?? null
    }
    setReminderRecord(initialRecord)
    setShowReminderModel(true)
  }

  const requestingModule = props.type === 'referral' ? ChildAuditAccessTypeEnum.Referral : ChildAuditAccessTypeEnum.Enrollment

  const reApply = async (): Promise<boolean> => {
    if (!isRequestLoading) {
      const submitValues = {
        referralId: props.referral.id,
        enrollmentId: props.enrollmentId
      }
      setIsRequestLoading(true)
      const { response } = await sendPost('/ReferralEdit/ReApplyIncomeRequest', submitValues)
      const rsp: boolean = response
      if (rsp) {
        toast.success('The income request was re-applied')
        props.onRefresh()
      } else {
        toast.error('The income request could not be re-applied')
      }
      setIsRequestLoading(false)
      return rsp
    }
    return false
  }

  return <>
    <Box sx={{ p: '0 24px' }}>
      <Typography component="div" variant="h5" fontWeight={'bold'}>{props.referral?.childName}</Typography>

      {perms.Enrollment_Communication && <Button onClick={setReminder} sx={{ textTransform: 'none' }} color='inherit'>
        <img src={stopwatch} alt="Set Reminder" />
        Set Reminder
      </Button>}
      <Button onClick={props.onPrint} color='inherit' sx={{ textTransform: 'none' }}>
        <img src={printer} alt="Print Details" />
        Print Details
      </Button>
      {props.referral.interestFormDetails != null && (
        <Button onClick={() => { setShowIntakeDetails(true) }} sx={{ textTransform: 'none' }}>
          <ViewListIcon />
          View Interest Form Details
        </Button>
      )}
      <Button startIcon={<AddIcon />} color='inherit' sx={{ textTransform: 'none' }} onClick={() => { props.onCreateNew?.(true) }}>
        Add Referral
      </Button>
      {props.referral.showCareManagement &&
        <CareManagementSummary childID={props.referral.childID} childName={props.referral?.childName} variant='chip' />
      }
      {props.type === 'enrollment' && (props.referral.status === 'Enrolled' || props.referral.status === 'Exited') &&
        <Button color='inherit' sx={{ textTransform: 'none' }} onClick={() => { setLogRestraint(true) }} data-testid='restraintButton'>Add Restraint Event</Button>
      }
      {props.type === 'referral' &&
        <ReferralHeader referral={props.referral} hideEditButtons={props.hideEditButtons} onEditHouseholdMembers={props.onEditHouseholdMembers} onEdit={props.onEdit} onRefresh={props.onRefresh} />
      }
      {props.type === 'enrollment' &&
        <EnrollmentHeader enrollment={props.referral} hideEditButtons={props.hideEditButtons} onEdit={props.onEdit} />
      }
    </Box>

    {props.referral.incomeApproved === 'Denied' && props.hideIncomeReapply !== true &&
      <Box sx={{ backgroundColor: 'lightgray', borderTop: '1px solid black', borderBottom: '1px solid black', display: 'grid', justifyContent: 'center' }}>
        <Typography>The income request was denied.</Typography>
        <Button variant='text' onClick={reApply}>Re-Apply</Button>
      </Box>
    }

    <Box sx={{ borderBottom: 1, borderColor: 'divider', p: '0 24px' }}>
      <Tabs value={value} onChange={(e, val) => { setValue(val) }}>
        <Tab sx={{ textTransform: 'none', fontWeight: 'bold' }} data-testid="details" label={'Details'} value={0} />
        <Tab sx={{ textTransform: 'none', fontWeight: 'bold' }} data-testid='documents' label={'Documents'} value={1} />
        <Tab sx={{ textTransform: 'none', fontWeight: 'bold' }} data-testid='notes' label={'Notes'} value={2} />
        {perms.CareNetwork_SupplementalNutrition && (<Tab sx={{ textTransform: 'none', fontWeight: 'bold' }} data-testid='nutrition' label='Supplemental Nutrition' value={3} />)}
      </Tabs>
    </Box>

    <Box sx={{ p: '0 24px' }}>
      <TabDisplay index={0} value={value}>
        <ChildDetails childID={props.referral.childID} requestingModule={requestingModule} isdid={props.referral.isdid} onRefresh={props.onRefresh} />
      </TabDisplay>
      <TabDisplay index={1} value={value}>
        {(props.type === 'referral' || props.referral.referralID !== null)
          ? <ReferralDocuments referralID={props.referral.referralID ?? props.referral.id}
              childID={props.referral.childID} dataChanged={props.onRefresh}
              readOnly={props.hideEditButtons || !(perms.Enrollment_Documentation_Edit || perms.CareNetwork_Documentation_Edit)}
              showChildDocsIfNoneOnReferral={props.type === 'enrollment'}
              years={props.referral.sessionBlockYears}
          />
          : <ChildAndGuardianDocumentList
              editable={(perms.Enrollment_Documentation_Edit ?? false) && !props.hideEditButtons}
              childID={props.referral.childID}
              isdID={props.referral.isdid}
              documentTypes={props.docTypes}
              dataChanged={props.onRefresh}
              isDocumentPermission={(perms.Enrollment_Documentation || perms.CareNetwork_Documentation_Edit) ?? false}
          />
        }
      </TabDisplay>
      <TabDisplay index={2} value={value}>
        {props.type === 'referral' && (
          <ReferralNotes
            referralID={props.referral.id}
            isEditable={perms.Enrollment_Communication_Edit || perms.CareNetwork_Enrollment_Edit}
            isNotesPermission={perms.Enrollment_Communication || perms.CareNetwork_Enrollment}
          />
        )}
        {props.type !== 'referral' && (
          <EnrollmentNotes
            enrollmentID={props.enrollmentId ?? 0}
            isEditable={perms.Enrollment_Communication_Edit || perms.CareNetwork_Enrollment_Edit}
            isNotesPermission={perms.Enrollment_Communication || perms.CareNetwork_Enrollment}
          />
        )}

      </TabDisplay>
      {perms.CareNetwork_SupplementalNutrition && (<TabDisplay index={3} value={value}>
        <SupplementalNutritionList childId={props.referral.childID} />
      </TabDisplay>)}

      {showReminderModel && reminderRecord !== null &&
        <CommunicationDetail
          open={showReminderModel}
          onClose={handleReminderClose}
          onSubmit={handleReminderAccept}
          isNew={true}
          selectedCommunication={reminderRecord}
          userOptions={reminderUsers}
        />
      }
      {logRestraint && props.enrollmentId != null && <RestraintEdit
        onClose={() => { setLogRestraint(false) }}
        editID={null}
        enrollmentID={props.enrollmentId}
      />}

    </Box>
    <BannerModal
      className='Nested Referral'
      title={'Interest Form Details'}
      onClose={() => { setShowIntakeDetails(false) }}
      open={showIntakeDetails}
      content={<Box sx={{ px: '24px' }}><InterestFormCard
        notes={props.referral.interestFormDetails?.notes ?? ''}
        services={props.referral.interestFormDetails?.interestedServices ?? []}
        onClick={() => {}}
      /></Box>}
      onConfirm={() => { setShowIntakeDetails(false) }}
      noButtons
    />
  </>
}
